import React, { useState, useEffect } from "react"
import { Flex, FlexProps, Text } from "@chakra-ui/core"
import Body from "../typography/Body"
import ModalButton from "../../components/ModalButton"

// --- CircleProgress ---
const CircleProgress = ({
  percent,
  size = 64,
  strokeWidth = 5,
  color = "#00C4D1",
}: {
  percent: number
  size?: number
  strokeWidth?: number
  color?: string
}) => {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (percent / 100) * circumference

  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      {/* Background Circle */}
      <circle
        r={radius}
        cx={size / 2}
        cy={size / 2}
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
        fill="transparent"
      />

      {/* Foreground Circle (Progress) */}
      <circle
        r={radius}
        cx={size / 2}
        cy={size / 2}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="transparent"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.35s ease" }}
      />
    </svg>
  )
}

// --- MatchBadge ---
const MatchBadge: React.FC<FlexProps & { percent: number; isBestMatch: boolean }> = ({
  percent,
  isBestMatch,
  ...props
}) => {
  const fontColor = isBestMatch ? "#ffffff" : "#00C4D1"

  // --- Responsive sizing ---
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 1024 : false)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const circleSize = isMobile ? 64 : 94
  const strokeWidth = isMobile ? 6 : 9
  const percentFontSize = isMobile ? "1rem" : "1.3rem"
  const matchFontSize = isMobile ? "0.65rem" : "0.9rem"

  return (
    <Flex
      position="relative"
      flexDir="column"
      alignItems="center"
      className="MatchBadgeWrapper"
      justifyContent="center"
      {...props}
    >
      <CircleProgress percent={percent} size={circleSize} strokeWidth={strokeWidth} color={fontColor} />

      {/* Centered Text */}
      <Flex
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        flexDir="column"
        alignItems="center"
        justifyContent="center"
      >
        <Text color={fontColor} fontFamily="din" fontSize={percentFontSize} fontWeight="bold">
          {percent}%
        </Text>
        <Body size="xs" fontSize={matchFontSize} color={fontColor}>
          Match!
        </Body>
      </Flex>
    </Flex>
  )
}

// --- FitBadge Props ---
export type FitBadgeProps = FlexProps & {
  fit?: "fit" | "no-fit" | null
  matchPercent?: number
  isBestMatch?: boolean
  isFeatured?: boolean
}

// --- FitBadge Component ---
export default function FitBadge({
  fit,
  matchPercent,
  isBestMatch = false,
  isFeatured = false,
  ...props
}: FitBadgeProps) {
  if (!fit && !matchPercent) return null

  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 1024 : false)
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return (
    <Flex justify="flex-end" {...props}>
      {matchPercent ? (
        <MatchBadge percent={matchPercent ?? 0} isBestMatch={isBestMatch} {...props} />
      ) : (
        <Flex
          h={isMobile ? "3rem" : "4rem"}
          w={isMobile ? "3rem" : "4rem"}
          backgroundColor={fit === "fit" ? "primaryLight7" : "sunriseLight7"}
          justifyContent="center"
          alignItems="center"
          p={isMobile ? "0.5rem" : "0.75rem 0.875rem"}
          borderRadius="0.5rem"
          {...props}
        >
          <Body size="xs" color={fit === "fit" ? "primary" : "sunrise"} textAlign="center">
            {fit === "fit" ? (
              <>
                {`It's a `}
                <Body
                  as="span"
                  display="block"
                  fontWeight="700"
                  fontFamily="DIN"
                  fontSize={isMobile ? "0.9rem" : "1.125rem"}
                >
                  Fit!
                </Body>
              </>
            ) : (
              `Not a Fit`
            )}
          </Body>
        </Flex>
      )}

      {isFeatured && (
        <Flex w="100%" mb="1.2875rem" mt="9rem" alignItems="center" flexDirection="column" gridRowGap="5px">
          <ModalButton
            iconName="testRide"
            text="30 Day Test Ride"
            title="30 Day Test Ride"
            modalBody={"What's this mean? Exactly what it says. We don't care what you do, ride It, store It, take it to buy groceries, get some exercise, the choice is yours. Either way if anytime between when you receive the bike and 30 Days you decide you don't love the experience, send it back, and we'll pay the shipping. We understand it may get some scratches, dents, and dings. We get it, you tested it. We don't charge restocking fees either. We're committed to you loving your bike, and don't want you keeping it unless you do. The 30 day return policy only applies to our standard analog bicycles; this return policy does not apply to our electric-powered bicycles. Please see below for the details regarding our electric bike return policy."}
          />
          <ModalButton
            iconName="shipping"
            text="Shipping Info"
            title="Shipping Info"
            modalBody={"Free Nationwide Shipping within the Continental United States. Alaska, and Hawaii are excluded from free shipping, however the rate will be discounted by the maximum amount of shipping cost in the continental USA. Alaska and Hawaii customers please, note that sixthreezero Electric Bicycles are larger, and heavier shipments and that will more to ship. We will ship to home and office directly via FedEx. All Bicycles arrive 80% assembled in box. Orders will generally ship within five business days and in most cases you will receive your order in about one week. For continental USA orders, we will pay to ship your bike to you, and in the case that you wish to return, we’ll pay to have it shipped back to sixthreezero."}
          />
          <ModalButton
            iconName="warranty"
            text="Warranty"
            title="Warranty"
            modalBody={"A One Year Standard Warranty is included with all sixthreezero bicycles when purchased new from sixthreezero.com. This warranty is limited to 1 Year from the date of delivery, so please register your bike the day you receive it. Proof of purchase will be required for any warranty claim within this 1 year period. Warranty is non-transferrable."}
          />
        </Flex>
      )}
    </Flex>
  )
}
